import { cn } from "@/lib/utils";

const BASE_CLASS =
  "inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap font-medium rounded-lg transition-[background-color,border-color,color,box-shadow,transform] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none active:translate-y-px [&_svg]:shrink-0";

/** Subtle top highlight + drop shadow that gives solid buttons depth. */
const SOLID_DEPTH_CLASS =
  "shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(16_24_40/0.12)]";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "error"
  | "success"
  | "accent";

export type ButtonSize =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "icon"
  | "icon-sm"
  | "icon-lg";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  // Solid variants use the theme-fixed `*-fill` tokens: white label >= 4.5:1 in both themes.
  primary: cn(
    "bg-gradient-to-b from-primary-fill to-primary-fill-strong text-white hover:from-primary-fill-strong hover:shadow-glow focus-visible:ring-primary-500",
    SOLID_DEPTH_CLASS,
  ),
  secondary:
    "bg-surface-100 text-surface-800 hover:bg-surface-200 focus-visible:ring-surface-400",
  outline:
    "border border-surface-200 bg-surface-0 text-surface-700 shadow-xs hover:bg-surface-50 hover:text-surface-900 hover:border-surface-300 dark:hover:bg-surface-100 focus-visible:ring-surface-400",
  ghost:
    "text-surface-600 hover:bg-surface-100 hover:text-surface-900 focus-visible:ring-surface-400",
  error: cn(
    "bg-error-fill text-white hover:bg-error-fill-strong focus-visible:ring-error-500",
    SOLID_DEPTH_CLASS,
  ),
  success: cn(
    "bg-success-fill text-white hover:bg-success-fill-strong focus-visible:ring-success-500",
    SOLID_DEPTH_CLASS,
  ),
  accent: cn(
    "bg-gradient-to-b from-accent-fill to-accent-fill-strong text-white hover:from-accent-fill-strong focus-visible:ring-accent-500",
    SOLID_DEPTH_CLASS,
  ),
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  xs: "h-7 px-2 text-xs gap-1 rounded-md",
  sm: "h-8 px-3 text-[13px] gap-1.5",
  md: "h-9 px-3.5 text-sm gap-2",
  lg: "h-10 px-4 text-sm gap-2",
  xl: "h-11 px-5 text-[15px] gap-2.5",
  icon: "h-9 w-9",
  "icon-sm": "h-8 w-8",
  "icon-lg": "h-10 w-10",
};

export function buttonVariants({
  variant = "primary",
  size = "md",
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} = {}): string {
  return cn(BASE_CLASS, VARIANT_CLASS[variant], SIZE_CLASS[size], className);
}
